// BOSS FORMS — looks (owner 2026-11-02: "bosses that transform mid-fight"). Render-only.
// Each boss's forms 2 and 3 are built from its own model (enemyArt/bosses.ts or the map's boss art) by:
//   • shedding parts (triangles removed by colour and / or place: a sand crust, a shield, an isle, a cloak),
//   • recolouring body and glow (exact colour swaps, like the map enemy skins),
//   • dressing new parts (a halo, a risen heart, ore spikes, orbiting rocks, a crown of flame) with the ArtBuilder,
// plus a per-form draw scale and glow strength (view.ts) and optional orbiting particles (bossFormFx.ts).
// Keys are the encounter names (sim.bossName / BOSS_FORMS in src/core/bossForms.ts). All designs are original.
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { ArtBuilder, at, type ArtGeo, type EnemyArt, limb, plate, type PartOpts } from './enemyArt/kit';

const PI = Math.PI;
const shard = (w: number, h: number, d = w) => new THREE.OctahedronGeometry(1, 0).scale(w, h / 2, d).translate(0, h / 2, 0);
const oct = (sx: number, sy: number, sz: number) => new THREE.OctahedronGeometry(1, 0).scale(sx, sy, sz);
const torus = (r: number, t: number, seg = 32, arc = PI * 2) => new THREE.TorusGeometry(r, t, 6, seg, arc);
const rock = (r: number, seed: number) => {
  const g = new THREE.IcosahedronGeometry(r, 0), p = g.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) { const k = 0.8 + 0.3 * Math.abs(Math.sin(p.getX(i) * 4.1 + seed) * Math.cos(p.getZ(i) * 3.3 + p.getY(i) * 2.2 + seed)); p.setXYZ(i, p.getX(i) * k, p.getY(i) * k, p.getZ(i) * k); }
  return g;
};

/** a triangle filter: body / glow colour (sRGB hex) and centroid → true = remove it */
type Shed = (hex: number, cx: number, cy: number, cz: number) => boolean;
export interface FormLook {
  /** draw scale (on top of the boss's own) and glow strength */
  scale: number; glow: number;
  swap?: Record<number, number>;
  /** glow recolour: exact swaps, or one colour for every glow part */
  glowSwap?: Record<number, number> | number;
  tint?: [number, number, number];
  shed?: Shed; shedGlow?: Shed;
  dress?: (b: ArtBuilder) => void;
  /** render-only orbiting motes / shards (bossFormFx.ts): count, radius, height, colour */
  orbit?: { n: number; r: number; y: number; color: number; size: number };
}

// ------------------------------------------------------------------------------------------------- geometry helpers
const _c = new THREE.Color();
function hexAt(col: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, i: number): number { _c.setRGB(col.getX(i), col.getY(i), col.getZ(i)); return _c.getHex(); }
/** drop every triangle the filter selects (ArtBuilder geometry is non-indexed: triangles are consecutive vertex triples) */
function shed(g: THREE.BufferGeometry, f: Shed): THREE.BufferGeometry {
  const pos = g.getAttribute('position'), col = g.getAttribute('color'); if (!pos || !col || g.index) return g;
  const keep: number[] = [];
  for (let i = 0; i + 2 < pos.count; i += 3) {
    const cx = (pos.getX(i) + pos.getX(i + 1) + pos.getX(i + 2)) / 3, cy = (pos.getY(i) + pos.getY(i + 1) + pos.getY(i + 2)) / 3, cz = (pos.getZ(i) + pos.getZ(i + 1) + pos.getZ(i + 2)) / 3;
    if (!f(hexAt(col, i), cx, cy, cz)) keep.push(i);
  }
  if (keep.length * 3 === pos.count) return g;
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(g.attributes)) {
    const a = g.getAttribute(name) as THREE.BufferAttribute, s = a.itemSize, src = a.array as Float32Array;
    const dst = new Float32Array(Math.max(1, keep.length) * 3 * s);
    let o = 0; for (const i of keep) { dst.set(src.subarray(i * s, (i + 3) * s), o); o += 3 * s; }
    out.setAttribute(name, new THREE.BufferAttribute(dst, s, a.normalized));
  }
  if (!keep.length) out.setDrawRange(0, 0);
  out.computeBoundingSphere();
  return out;
}
function recolor(g: THREE.BufferGeometry, swap: Record<number, number> | number | undefined, tint?: [number, number, number]): void {
  const c = g.getAttribute('color') as THREE.BufferAttribute | undefined; if (!c || (swap === undefined && !tint)) return;
  const map = typeof swap === 'object' ? new Map(Object.entries(swap).map(([a, b]) => [Number(a), new THREE.Color(b)])) : null;
  const one = typeof swap === 'number' ? new THREE.Color(swap) : null;
  for (let i = 0; i < c.count; i++) {
    _c.setRGB(c.getX(i), c.getY(i), c.getZ(i));
    if (one) _c.copy(one); else if (map) { const to = map.get(_c.getHex()); if (to) _c.copy(to); }
    if (tint) _c.setRGB(_c.r * tint[0], _c.g * tint[1], _c.b * tint[2]);
    c.setXYZ(i, _c.r, _c.g, _c.b);
  }
  c.needsUpdate = true;
}
/** the boss's model with a form's changes applied */
export function applyLook(geo: ArtGeo, L: FormLook): ArtGeo {
  let body = geo.body, glow = geo.glow;
  if (L.shed) body = shed(body, L.shed);
  if (L.shedGlow) glow = shed(glow, L.shedGlow);
  recolor(body, L.swap, L.tint);
  recolor(glow, L.glowSwap);
  if (L.dress) {
    const b = new ArtBuilder(); L.dress(b);
    const x = b.build();
    for (const part of ['body', 'glow'] as const) {
      const add = x[part]; if ((add.getAttribute('position')?.count ?? 0) <= 3) continue;
      const base = part === 'body' ? body : glow;
      const m = mergeGeometries([base, add], false);
      if (m) { if (part === 'body') body = m; else glow = m; }
    }
  }
  body.computeBoundingSphere(); glow.computeBoundingSphere();
  return { body, glow };
}

// ------------------------------------------------------------------------------------------------- the looks
const G = (o: Omit<PartOpts, 'glow'>): PartOpts => ({ ...o, glow: true });
/** n shards standing in a ring (crowns, halos) */
function crown(b: ArtBuilder, n: number, r: number, y: number, z: number, h: number, w: number, cols: number[], o: Partial<PartOpts> = {}, glow = true, tilt = 0.25): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * PI * 2, g = shard(w, h * (i % 2 ? 0.75 : 1), w * 0.7); g.rotateX(-tilt); g.rotateY(a);
    b.add(at(g, Math.sin(a) * r, y, z + Math.cos(a) * r), { color: cols[i % cols.length], ...o, glow });
  }
}
/** a loose ring of floating rocks / shards around the body (static geometry; bossFormFx adds moving motes) */
function floaters(b: ArtBuilder, n: number, r: number, y: number, size: number, col: number, top?: number, seed = 1): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * PI * 2 + seed, yy = y + Math.sin(i * 2.3 + seed) * 0.9, s = size * (0.7 + 0.5 * Math.abs(Math.sin(i * 1.7 + seed)));
    b.add(at(rock(s, i + seed), Math.cos(a) * r, yy, Math.sin(a) * r, i, i * 0.7, 0), { color: col });
    if (top !== undefined) b.add(at(new THREE.CylinderGeometry(s * 0.85, s * 0.75, s * 0.3, 7), Math.cos(a) * r, yy + s * 0.75, Math.sin(a) * r), { color: top });
  }
}

// Hollow Tyrant palette (enemyArt/bosses.ts)
const HT = { BAS: 0x2a2830, BAS2: 0x45404e, ARM: 0x5c5568, BONE: 0xd8ccb0, GOLD: 0xd8b050, CLOAK: 0x2a1a2e, MAG: 0xff3ca8 };
// Tyrant Guardian
const TG = { STEEL: 0x4a5266, DARK: 0x262b38, IVORY: 0xe8dcc0, CRIM: 0x8a2234, TEAL: 0x3fe0d0, BLADE: 0xb8c0cc };
// Ironhide Colossus
const IC = { IRON: 0x2c2f38, IRON2: 0x474b57, PLATE: 0x8d8578, RUST: 0x9a5a2c, EMBER: 0xff7a2a };
// Prism Sovereign
const PS = { BODY: 0x3a4258, CRY: 0x5fd8e8, CRY2: 0x9ff0ff, ROBE: 0x232a4a, BRASS: 0xc89a48 };
// Duneglass Behemoth (maps/crystal-desert/boss.ts)
const DG = { PLATE: 0xd2aa6c, PLATE2: 0xb08650, DARK: 0x5a4250, UNDER: 0x3a2e40, VIO: 0x8a58e8, TEAL: 0x34cfc8, GT: 0x8ffff2, GV: 0xd6a8ff };
// Hoarstone Keeper (maps/glacier-ruins/boss.ts)
const HK = { STONE: 0x4a5470, STONE_D: 0x283044, STONE_L: 0x66718e, ICE: 0x86c8f6, ICE_D: 0x3a78bc, SNOW: 0xeef4ff, GLOW: 0xa8f8ff, RUNE: 0xb890ff };
// Curfew Bastion (maps/shattered-metropolis/foes.ts)
const CBn = { CONC: 0xbdb3a0, CONC2: 0x8e877b, ROAD: 0x45484f, AMBER: 0xffae40, DIAL: 0xff5aa8, UNLIT: 0x1a2430, STEEL: 0x35605e };
/** one broken overpass slab hanging in the air (ZERO HOUR): built flat in slab space, then placed (x, y, z, rx, ry, rz) */
function deckSlab(b: ArtBuilder, x: number, y: number, z: number, rx: number, ry: number, rz: number, w: number, d: number): void {
  const put = (g: THREE.BufferGeometry, lx: number, ly: number, lz: number, lrz = 0) => at(at(g, lx, ly, lz, 0, 0, lrz), x, y, z, rx, ry, rz);
  b.add(put(plate(w, 0.42, d, 0.06), 0, 0, 0), { color: 0x5a5d66 });                                  // asphalt deck
  b.add(put(plate(w * 1.02, 0.22, d * 1.02, 0.04), 0, -0.3, 0), { color: CBn.CONC });                // concrete underside
  for (const sz of [-1, 1]) b.add(put(plate(w, 0.32, 0.2, 0.04), 0, 0.34, sz * (d / 2 - 0.1)), { color: CBn.CONC }); // barrier rails
  for (const lx of [-0.5, 0.5]) b.add(put(plate(0.5, 0.05, 0.16, 0.02), lx * w * 0.6, 0.23, 0), { color: 0xf0c860 }); // lane dashes
  const E = 0xffc878, t = 0.09;                                                                        // lit broken edges
  for (const sz of [-1, 1]) b.add(put(plate(w + t, t, t, 0.02), 0, -0.05, sz * (d / 2 + 0.02)), G({ color: E }));
  for (const sx of [-1, 1]) b.add(put(plate(t, t, d + t, 0.02), sx * (w / 2 + 0.02), -0.05, 0), G({ color: sx < 0 ? E : 0xff7a50 }));
  for (const sx of [-1, 1]) b.add(put(plate(0.05, 0.5, 0.05, 0.01), sx * w * 0.42, -0.62, d * 0.2, sx * 0.3), { color: 0xa0522d }); // snapped rebar
}
// Thrumback Behemoth (maps/verdant-alien/boss.ts)
const TB = { SLATE: 0x3a3458, SLATE2: 0x544a7c, PLATE: 0x6c5e98, FACET: 0x7a6cae, MOSS: 0x4fa86a, VIO: 0x9a6cff, VIO2: 0x6a44d0, CYAN: 0x7af4ff, RING: 0xc8a0ff };
// Cloudhull Leviathan (maps/floating-isles/boss.ts)
const CL = { SLATE: 0x7684a6, SLATE2: 0x56628a, BELLY: 0xdde4ee, GOLD: 0xe2b850, CLOUD: 0xf6f9ff, CRY: 0x8ff0ff, EYE: 0xffd870, SOIL: 0x6a5440, TURF: 0x6cc254 };
// Kilnheart Titan (maps/ember-wastes/boss.ts)
const KT = { BAS: 0x4a2e26, BAS2: 0x5e3a30, BAS3: 0x6e4c40, OBS: 0x1a1420, MAG: 0xff6a1a, HOT: 0xffc040 };
// Oreheart Behemoth (maps/rust-canyon/rcBoss.ts)
const OH = { MAROON: 0x7a3024, RUST: 0xa54a2c, ORANGE: 0xd47e44, CREAM: 0xedcaa0, SALMON: 0xc96c4a, CAP: 0x7e4630, ORE: 0x3a2622, ORE2: 0x5e3a2c, GLOW: 0xff8a2a, HOT: 0xffd27a };

export const FORM_LOOKS: Record<string, [FormLook, FormLook]> = {
  'HOLLOW TYRANT': [
    { // SHATTERED CROWN: the crown fragments lock into a full, turning halo; the core blazes through the ribs
      scale: 1.06, glow: 1.4, tint: [1.05, 1.0, 1.0],
      dress: (b) => {
        const cr: PartOpts = { color: HT.GOLD, pivot: [0, 6.4, 0.2], raise: -0.25 };
        b.add(at(torus(1.95, 0.1, 40), 0, 7.65, 0.05, PI / 2 - 0.1), G({ ...cr, color: 0xffc84a }));
        crown(b, 10, 1.95, 7.6, 0.05, 1.5, 0.26, [0xffd870, 0xffc84a], cr, true, 0.1);
        b.add(at(oct(0.85, 1.25, 0.75), 0, 5.15, 0.5, 0.25, 0.4, 0.12), G({ color: 0xffd0ff }));
        for (const s of [-1, 1]) b.add(at(plate(0.1, 1.2, 0.1, 0.03), s * 0.7, 5.3, 0.9, 0, 0, s * 0.7), G({ color: 0xffc84a })); // core light leaking through the ribs
      },
      orbit: { n: 10, r: 2.2, y: 7.7, color: 0xffd060, size: 0.35 },
    },
    { // HOLLOW STAR: the cloak burns away, the core rises above the shoulders as a burning star
      scale: 1.12, glow: 1.8, swap: { [HT.BAS]: 0x1c1622, [HT.ARM]: 0x40324e, [HT.BAS2]: 0x342a3e }, glowSwap: { [HT.MAG]: 0xff70f0 },
      shed: (h) => h === HT.CLOAK,
      dress: (b) => {
        b.add(at(new THREE.IcosahedronGeometry(1.0, 1), 0, 9.0, -0.2), G({ color: 0xffd8ff }));
        for (let i = 0; i < 10; i++) { const a = (i / 10) * PI * 2, g = shard(0.22, 1.6 + (i % 2) * 0.8, 0.18); g.rotateZ(PI / 2); g.rotateY(a); b.add(at(g, Math.cos(a) * 0.6, 9.0, -0.2 - Math.sin(a) * 0.6), G({ color: i % 2 ? 0xff70f0 : 0xffc0ff })); }
        b.add(at(torus(1.9, 0.08, 40), 0, 9.0, -0.2, PI / 2 + 0.35), G({ color: 0xff70f0 }));
        for (const [x, y, rz, l] of [[-0.9, 4.2, 0.6, 1.4], [0.9, 4.0, -0.5, 1.3], [-1.6, 6.1, 1.0, 1.0], [1.5, 6.0, -1.1, 1.0], [0, 3.6, 0, 0.9]] as const)
          b.add(at(plate(0.12, l, 0.12, 0.03), x, y, 0.95, 0.15, 0, rz), G({ color: 0xff70f0 })); // cracks
        b.add(limb([0, 6.9, 0.2], [0, 8.2, -0.15], 0.14, 0.05, 6), G({ color: 0xff9af6 })); // the tether between head and star
      },
      orbit: { n: 14, r: 4.2, y: 5.2, color: 0xff70f0, size: 0.4 },
    },
  ],
  'TYRANT GUARDIAN': [
    { // SUNDERED SHIELD: the shield cracks along its scar, teal light pours out
      scale: 1.05, glow: 1.5,
      dress: (b) => {
        const so: PartOpts = { color: TG.TEAL, pivot: [1.8, 1.25, 1.2], swing: 0.05, sphase: PI, raise: 0.38 };
        for (const [x, y, rz, l] of [[2.4, 2.4, 0.5, 2.2], [1.7, 3.6, -0.7, 1.6], [2.6, 4.3, 0.2, 1.2], [1.9, 1.6, 1.2, 1.0]] as const)
          b.add(at(plate(0.12, l, 0.1, 0.03), x, y, 1.52, 0, -0.18, rz), G(so));
        b.add(at(oct(0.45, 0.6, 0.18), 2.1, 3.45, 1.58, 0, -0.18, 0), G({ ...so, color: 0xb0fff4 }));
        b.add(at(plate(0.22, 4.0, 0.12, 0.04), 2.05, 3.3, 1.56, 0, -0.18, 0.06), G({ ...so, color: 0xb0fff4 })); // the split down the middle
        // shield fragments breaking away (owner 2026-11-02 'barely show'): bigger ivory shards held clear of the shield's
        // outer edge (x 3.35) in front of it, each framed by a teal glow plate behind it and joined to the edge by a light shaft
        for (const [x, y, z, rz, w, h] of [[4.2, 4.55, 1.62, -0.42, 1.05, 1.3], [4.45, 2.85, 1.55, 0.35, 0.95, 1.15], [3.85, 1.55, 1.7, -0.2, 0.8, 0.9], [3.6, 6.05, 1.45, -0.85, 0.85, 1.0]] as const) {
          b.add(at(plate(w, h, 0.2, 0.06), x, y, z + 0.06, 0.12, -0.3, rz), { ...so, color: TG.IVORY, glow: false });
          b.add(at(plate(w * 0.55, 0.07, 0.05, 0.02), x, y + h * 0.12, z + 0.19, 0.12, -0.3, rz + 0.5), G({ ...so, color: 0xb0fff4 })); // crack across the face
          b.add(at(plate(w + 0.26, h + 0.26, 0.08, 0.03), x, y, z - 0.1, 0.12, -0.3, rz), G(so)); // glowing rim (light through the break)
          b.add(at(plate(Math.max(0.2, x - 3.3), 0.09, 0.06, 0.02), (x + 3.3) / 2, y - 0.05, z - 0.05, 0, -0.3, rz * 0.3), G({ ...so, color: 0x8affee })); // shaft from the edge
        }
        for (const s of [-1, 1]) b.add(at(new THREE.ConeGeometry(0.28, 1.2, 6), s * 1.6, 5.75, 0, 0, 0, s * -0.3), G({ color: 0x5cffe8 })); // teal flames off the pauldrons
        b.add(at(plate(0.9, 0.1, 0.1, 0.03), 0, 5.8, 0.83), G({ color: 0x5cffe8 })); // visor blaze
      },
      orbit: { n: 6, r: 2.6, y: 3.5, color: 0x5cffe8, size: 0.3 },
    },
    { // OATHBREAKER: the broken shield is cast aside; the cleaver blazes, two-handed
      scale: 1.08, glow: 1.7, glowSwap: { [TG.TEAL]: 0xff5a7a }, swap: { [TG.CRIM]: 0xc0283c },
      shed: (_h, cx, _cy, cz) => cx > 0.9 && cz > 0.8, shedGlow: (_h, cx, _cy, cz) => cx > 0.9 && cz > 0.8,
      dress: (b) => {
        const o: PartOpts = { color: 0xff5a7a, pivot: [-1.45, 4.8, 0], swing: 0.16, sphase: 0, raise: -0.35 };
        const blaze = plate(0.3, 4.1, 0.22, 0.05); blaze.translate(-0.42, 2.05, 0);
        b.add(at(blaze, -1.75, 3.55, 1.2, 0.28, 0, 0.42), G(o));
        const core = plate(0.5, 3.6, 0.08, 0.03); core.translate(0.12, 2.0, 0);
        b.add(at(core, -1.75, 3.55, 1.2, 0.28, 0, 0.42), G({ ...o, color: 0xffb0c0 }));
        b.add(limb([1.55, 4.75, 0], [0.2, 3.3, 0.9], 0.3, 0.27), { color: TG.DARK, pivot: [1.45, 4.75, 0], swing: 0.05, sphase: PI }); // the freed arm reaches for the hilt
        for (const s of [-1, 1]) b.add(at(shard(0.16, 1.3, 0.12), s * 0.5, 6.0, -0.1, -0.3, 0, s * -0.5), { color: TG.CRIM }); // crest horns
      },
      orbit: { n: 8, r: 3.0, y: 2.0, color: 0xff5a7a, size: 0.32 },
    },
  ],
  'IRONHIDE COLOSSUS': [
    { // STOKED FURNACE: stacks roar white-hot
      scale: 1.04, glow: 1.6, glowSwap: { [IC.EMBER]: 0xffb040 },
      dress: (b) => {
        for (const s of [-1, 1]) {
          b.add(at(new THREE.ConeGeometry(0.55, 2.4, 8), s * 1.45, 11.0, -1.45), G({ color: 0xffd060 }));
          b.add(at(new THREE.ConeGeometry(0.3, 1.6, 8), s * 1.45, 11.3, -1.45), G({ color: 0xfff0b0 }));
        }
      },
      orbit: { n: 8, r: 3.5, y: 9.5, color: 0xffb040, size: 0.35 },
    },
    { // SLAG BREACH: chest armour falls away over a molten core
      scale: 1.08, glow: 1.9, swap: { [IC.IRON]: 0x3a2420, [IC.PLATE]: 0x6a4a3a, [IC.IRON2]: 0x4e3430 }, glowSwap: { [IC.EMBER]: 0xff5020 },
      shed: (h, _cx, cy, cz) => (h === IC.PLATE || h === IC.RUST) && cz > 2.6 && cy > 3.8,
      dress: (b) => {
        b.add(at(new THREE.IcosahedronGeometry(1.35, 1), 0, 5.6, 2.55), G({ color: 0xff5020 }));
        b.add(at(new THREE.IcosahedronGeometry(0.8, 1), 0, 5.7, 3.1), G({ color: 0xffd080 }));
        for (const [x, y, z, rz] of [[-2.0, 5.5, 2.4, 0.6], [2.0, 5.3, 2.4, -0.6], [-1.2, 7.4, 1.4, 1.2], [1.3, 7.3, 1.4, -1.1], [0, 4.0, 3.0, 0]] as const)
          b.add(at(plate(0.14, 1.6, 0.14, 0.04), x, y, z, 0.3, 0, rz), G({ color: 0xff6a20 }));
      },
      orbit: { n: 10, r: 5, y: 6, color: 0xff5020, size: 0.45 },
    },
  ],
  'PRISM SOVEREIGN': [
    { // REFRACTED: the prisms brighten and a light halo opens behind the mask
      scale: 1.05, glow: 1.6, swap: { [PS.CRY]: 0x8ff4ff },
      dress: (b) => {
        b.add(at(torus(1.5, 0.1, 40), 0, 6.1, -0.45), G({ color: 0x9ff0ff }));
        crown(b, 8, 1.5, 5.75, -0.45, 0.8, 0.12, [0x9ff0ff, 0xe0ffff], {}, true, 0);
        for (const s of [-1, 1]) b.add(at(shard(0.3, 1.6, 0.24), s * 1.7, 5.3, -0.1, 0, 0, s * -0.75), G({ color: 0x9ff0ff, pivot: [s * 0.95, 5.05, 0], raise: -0.35 })); // prisms split into light blades
      },
      orbit: { n: 6, r: 2.0, y: 5.0, color: 0x9ff0ff, size: 0.3 },
    },
    { // SPECTRUM CROWN: a crown of prisms ignites over the mask
      scale: 1.12, glow: 1.9, swap: { [PS.CRY]: 0xc890ff, [PS.ROBE]: 0x2a1840 }, glowSwap: { [PS.CRY2]: 0xe0b0ff },
      dress: (b) => {
        crown(b, 7, 0.95, 6.75, 0.1, 1.3, 0.16, [0xd890ff, 0x9ff0ff, 0xffd870], {}, true, 0.3);
        b.add(at(torus(0.95, 0.06, 32), 0, 6.8, 0.1, PI / 2), G({ color: 0xd890ff }));
        b.add(at(torus(1.6, 0.06, 40), 0, 5.75, -1.0), G({ color: 0xd890ff }));
      },
      orbit: { n: 10, r: 2.8, y: 4.5, color: 0xd890ff, size: 0.32 },
    },
  ],
  'DUNEGLASS BEHEMOTH': [
    { // GLASSHEART: the sand crust sloughs away into living glass around a burning crystal heart
      scale: 1.05, glow: 1.6, swap: { [DG.PLATE]: 0x8adcd8, [DG.PLATE2]: 0x5ab0c4, [DG.DARK]: 0x3a4a6a },
      dress: (b) => {
        b.add(at(oct(1.1, 1.6, 0.9), 0, 2.9, 1.4, 0, PI / 4, 0), G({ color: DG.GT }));
        b.add(at(oct(0.55, 0.85, 0.45), 0, 3.05, 2.2, 0, PI / 4, 0), G({ color: 0xffffff }));
        for (const [z, w] of [[-1.6, 3.6], [-3.45, 2.9], [-5.1, 2.0]] as const) b.add(at(plate(w, 0.14, 0.14, 0.04), 0, 3.0 + z * 0.12, z - 1.1), G({ color: DG.GT })); // glass seams
      },
      orbit: { n: 8, r: 5.5, y: 3, color: DG.GT, size: 0.35 },
    },
    { // SHARDSTORM: the glass body bursts; a storm of shards orbits the heart
      scale: 1.1, glow: 2.0, swap: { [DG.PLATE]: 0x9a78e8, [DG.PLATE2]: 0x6a58c8, [DG.UNDER]: 0x2a2048 }, glowSwap: { [DG.GT]: DG.GV },
      shed: (h, _cx, cy, cz) => (h === DG.PLATE || h === DG.PLATE2) && cz < -0.6 && cy > 1.2,
      dress: (b) => {
        b.add(at(oct(1.3, 1.9, 1.1), 0, 3.6, -1.6, 0, PI / 4, 0), G({ color: 0xffffff }));
        b.add(at(oct(1.8, 2.4, 1.5), 0, 3.6, -1.6, 0, 0, 0), G({ color: DG.GV }));
        for (let i = 0; i < 14; i++) {
          const a = (i / 14) * PI * 2, r = 5.2 + (i % 3) * 0.8, y = 2.5 + Math.sin(i * 1.9) * 1.6;
          const g = shard(0.32, 1.5 + (i % 2) * 0.8, 0.26); g.rotateZ(PI / 2 - 0.4); g.rotateY(-a);
          b.add(at(g, Math.cos(a) * r, y, -1.6 + Math.sin(a) * r), { color: i % 2 ? DG.VIO : DG.TEAL });
        }
      },
      orbit: { n: 18, r: 6.5, y: 3.2, color: DG.GV, size: 0.45 },
    },
  ],
  'HOARSTONE KEEPER': [
    { // RIMEWAKE: rime armour thickens; the broken ring lights up
      scale: 1.05, glow: 1.6, glowSwap: { [HK.RUNE]: 0xc8f4ff },
      dress: (b) => {
        const ring: PartOpts = { color: 0xc8f4ff, pivot: [0, 6.2, -1.0], raise: 0.3 };
        b.add(at(torus(2.3, 0.12, 40, PI * 1.55), 0, 7.4, -1.3, 0, 0, -PI * 0.27 + 0.2), G(ring));
        for (const s of [-1, 1]) for (let k = 0; k < 3; k++) b.add(at(shard(0.24, 1.2 + k * 0.4, 0.2), s * (1.6 + k * 0.45), 6.7 + k * 0.1, -0.5 - k * 0.2, -0.3, 0, s * -(0.3 + k * 0.25)), { color: HK.ICE });
        b.add(at(plate(2.4, 1.9, 0.25, 0.12), 0, 5.1, 1.1, -0.06), { color: 0x9ad6ff }); // a new rime breastplate
      },
      orbit: { n: 8, r: 3, y: 7.4, color: 0xc8f4ff, size: 0.3 },
    },
    { // WINTER HEART: the chest slabs fall, the frozen heart rises into the ring as a cold star; icicles crown the head
      scale: 1.1, glow: 1.9, swap: { [HK.STONE]: 0x3a4466, [HK.STONE_L]: 0x56628a }, glowSwap: { [HK.GLOW]: 0xd8c8ff },
      shed: (h, cx, cy, cz) => (h === HK.ICE || h === HK.ICE_D) && cz > 0.7 && cy > 3.9 && cy < 6.3 && Math.abs(cx) < 1.6,
      dress: (b) => {
        const ring: PartOpts = { color: HK.RUNE, pivot: [0, 6.2, -1.0], raise: 0.3 };
        b.add(at(new THREE.IcosahedronGeometry(0.85, 0), 0, 7.4, -1.3), G({ ...ring, color: 0xd8c8ff }));
        for (let i = 0; i < 8; i++) { const a = (i / 8) * PI * 2, g = shard(0.12, 1.1, 0.1); g.rotateZ(PI / 2); g.rotateY(a); b.add(at(g, Math.cos(a) * 0.4, 7.4, -1.3 - Math.sin(a) * 0.4), G({ ...ring, color: HK.RUNE })); }
        crown(b, 7, 0.45, 8.1, 0.35, 2.0, 0.13, [HK.ICE, 0xc6ecff], {}, false, 0.2);
        b.add(at(oct(0.35, 0.55, 0.2), 0, 5.2, 1.05, 0, 0.4, 0), G({ color: 0xd8c8ff })); // the hollow where the heart was
      },
      orbit: { n: 12, r: 4, y: 6.5, color: 0xd8c8ff, size: 0.35 },
    },
  ],
  'CURFEW BASTION': [
    { // LOCKDOWN: every window blazes, sirens on the shoulders
      scale: 1.04, glow: 1.6, glowSwap: { [CBn.AMBER]: 0xff8ac0 },
      dress: (b) => {
        for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) {
          if ((r === 1 && (c === 1 || c === 2)) || (r * 4 + c) % 3 !== 1) continue;
          const y = 5.75 + r * 0.95; b.add(at(plate(0.58, 0.58, 0.1, 0.03), -1.35 + c * 0.9, y, 1.57 + (y - 5) * 0.06, 0.06), G({ color: 0xff5aa8 }));
        }
        for (const s of [-1, 1]) b.add(at(new THREE.SphereGeometry(0.32, 10, 8), s * 2.75, 9.05, 0.6), G({ color: 0xff3040 }));
        b.add(at(new THREE.SphereGeometry(0.3, 10, 8), 1.75, 13.3, -1.0), G({ color: 0xff3040 }));
      },
      orbit: { n: 6, r: 3.5, y: 9.5, color: 0xff5aa8, size: 0.3 },
    },
    { // ZERO HOUR: the tower splits around the dial; the overpass breaks and its slabs hang in the air
      scale: 1.08, glow: 1.9, swap: { [CBn.CONC]: 0x9a8f80, [CBn.CONC2]: 0x6e675d }, glowSwap: { [CBn.AMBER]: 0xff3040, [CBn.DIAL]: 0xff7ac0 },
      shed: (_h, cx, cy) => cy > 8.7 && cy < 10.0 && Math.abs(cx) > 2.3,
      dress: (b) => {
        for (const [x, y, z, rz] of [[0.15, 7.4, 1.62, 0.15], [-0.25, 5.6, 1.6, -0.25], [0.5, 4.9, 1.6, 0.4]] as const) b.add(at(plate(0.16, 1.9, 0.1, 0.03), x, y, z, 0.06, 0, rz), G({ color: 0xff7ac0 }));
        b.add(at(new THREE.CylinderGeometry(0.85, 0.85, 0.1, 22), 0, 6.75, 1.7, PI / 2 + 0.06), G({ color: 0xff3060 }));
        // the broken deck (owner 2026-11-02 'read as dark spikes'): flat road slabs tipped toward the viewer, concrete sides and
        // underside, a dashed lane mark on top, lit (glowing) broken edges all round so they read as slabs, not spikes
        for (const s of [-1, 1]) for (let k = 0; k < 2; k++) deckSlab(b, s * (3.9 + k * 1.7), 10.7 + k * 1.0, 0.5 + k * 0.3, 0.42 + 0.1 * k, s * 0.25 * k, s * (0.16 + k * 0.12), 2.0 - k * 0.2, 2.3);
      },
      orbit: { n: 8, r: 5, y: 10, color: 0xff3040, size: 0.4 },
    },
  ],
  'THRUMBACK BEHEMOTH': [
    { // RESONANT: the grove surges taller; a third ring hums
      scale: 1.05, glow: 1.6,
      dress: (b) => {
        for (const [x, z, h, rx, rz] of [[0.6, 0.4, 5.2, 0.1, -0.15], [-0.5, -0.5, 4.8, -0.15, 0.2], [1.1, -1.2, 4.0, -0.3, -0.3], [-1.2, 1.0, 3.8, 0.3, 0.35]] as const) {
          const g = new THREE.LatheGeometry([new THREE.Vector2(0, 0), new THREE.Vector2(0.5, 0), new THREE.Vector2(0.45, h * 0.78), new THREE.Vector2(0, h)], 6);
          b.add(at(g, x, 6.6, z, rx, 0.4, rz), { color: TB.VIO });
          b.add(at(oct(0.3, 0.6, 0.3), x + Math.sin(rz) * -h * 0.95, 6.6 + h, z + Math.sin(rx) * h * 0.95), G({ color: TB.CYAN }));
        }
        b.add(at(torus(4.0, 0.08, 48), 0, 9.4, -0.1, PI / 2, 0, 0.05), G({ color: 0x7af4ff, pivot: [0, 7.6, 0], raise: -0.35 }));
      },
      orbit: { n: 10, r: 4.2, y: 8.5, color: TB.CYAN, size: 0.3 },
    },
    { // GEODE BLOOM: the shell splits open, the singing heart floats free inside spinning rings
      scale: 1.1, glow: 2.0, glowSwap: { [TB.CYAN]: 0xff8af0, [TB.RING]: 0xff8af0 },
      shed: (h, _cx, cy) => (h === TB.SLATE || h === TB.SLATE2 || h === TB.FACET || h === TB.MOSS) && cy > 5.4,
      dress: (b) => {
        b.add(at(new THREE.IcosahedronGeometry(1.4, 1), 0, 8.6, 0), G({ color: 0xff8af0 }));
        b.add(at(new THREE.IcosahedronGeometry(0.8, 0), 0, 8.6, 0), G({ color: 0xffffff }));
        b.add(at(torus(2.2, 0.08, 40), 0, 8.6, 0, PI / 2 + 0.5, 0, 0.3), G({ color: 0xff8af0 }));
        b.add(at(torus(2.8, 0.07, 44), 0, 8.6, 0, PI / 2 - 0.4, 0, -0.4), G({ color: 0x7af4ff }));
        for (let i = 0; i < 6; i++) { const a = (i / 6) * PI * 2; b.add(at(plate(1.6, 0.35, 1.0, 0.1), Math.cos(a) * 2.6, 5.6, Math.sin(a) * 3.0, Math.sin(a) * 0.9, -a, Math.cos(a) * 0.9), { color: TB.SLATE2 }); } // shell petals peeled open
      },
      orbit: { n: 14, r: 3.4, y: 8.6, color: 0xff8af0, size: 0.35 },
    },
  ],
  'CLOUDHULL LEVIATHAN': [
    { // STORMHULL: the cloud skirt turns to a thunderhead, the rune bands blaze
      scale: 1.05, glow: 1.7, swap: { [CL.CLOUD]: 0x4a5068, [CL.BELLY]: 0xa4acc0, [CL.SLATE]: 0x5e6a8e }, glowSwap: { [CL.CRY]: 0xffe070 },
      dress: (b) => {
        for (const z of [-3.0, -1.0, 1.0]) { const f = Math.sqrt(Math.max(0.15, 1 - ((z + 0.4) / 4.6) ** 2)); b.add(at(new THREE.TorusGeometry(1, 0.08, 6, 28), 0, 2.65, z, 0, 0, 0, [2.47 * f, 1.92 * f, 1]), G({ color: 0xffe070 })); }
        for (const [x, y, z] of [[1.0, 0.3, -1.5], [-1.2, 0.2, 0.6], [0.2, 0.1, -3.5]] as const) b.add(at(plate(0.12, 1.4, 0.12, 0.03), x, y, z, 0.2, 0, x * 0.4), G({ color: 0xfff4b0 })); // lightning under the hull
      },
      orbit: { n: 8, r: 5.5, y: 1.5, color: 0xffe070, size: 0.35 },
    },
    { // BROKEN SKY: the isle tears loose and circles it as a ring of floating rocks
      scale: 1.1, glow: 1.9, glowSwap: { [CL.CRY]: 0x6cc2ff, [CL.EYE]: 0xc8ecff },
      shed: (_h, _cx, cy, cz) => cy > 4.05 && cz < 1.9, shedGlow: (_h, _cx, cy, cz) => cy > 3.0 && cz < 1.9 && cz > -1.5,
      dress: (b) => { floaters(b, 8, 5.8, 5.5, 0.9, CL.SOIL, CL.TURF, 0.4); b.add(at(torus(5.8, 0.06, 64), 0, 5.4, 0, PI / 2), G({ color: 0x6cc2ff })); },
      orbit: { n: 12, r: 5.8, y: 5.5, color: 0x6cc2ff, size: 0.4 },
    },
  ],
  'KILNHEART TITAN': [
    { // MOLTEN HEART: the chest splits wide, the furnace heart rises out of it, magma pours down the arms
      scale: 1.06, glow: 1.8, glowSwap: { [KT.MAG]: 0xff8a20 },
      dress: (b) => {
        b.add(at(new THREE.IcosahedronGeometry(1.1, 1), 0, 6.3, 1.75), G({ color: 0xff7a1a }));
        b.add(at(new THREE.IcosahedronGeometry(0.6, 1), 0, 6.35, 2.4), G({ color: 0xff9a30 }));
        for (let i = 0; i < 6; i++) { const a = (i / 6) * PI * 2, g = shard(0.14, 1.1, 0.12); g.rotateX(PI / 2); g.rotateY(a); b.add(at(g, 0, 6.3, 1.75), G({ color: 0xff8a20 })); }
        for (const s of [-1, 1]) {
          const a: PartOpts = { color: 0xff8a20, pivot: [s * 2.6, 6.0, -0.1], swing: 0.2, sphase: s > 0 ? PI : 0, raise: -1.05 };
          b.add(limb([s * 2.7, 5.9, 0.4], [s * 3.45, 3.8, 0.9], 0.16, 0.12, 6), G(a));
          b.add(limb([s * 3.45, 3.8, 0.9], [s * 3.6, 1.9, 1.4], 0.12, 0.18, 6), G(a));
        }
      },
      orbit: { n: 10, r: 3.5, y: 6.5, color: 0xffa030, size: 0.35 },
    },
    { // MAGMA COLOSSUS: the basalt shell melts into a giant of living magma under a crown of flame
      scale: 1.14, glow: 2.1, swap: { [KT.BAS]: 0xa83a18, [KT.BAS2]: 0xc24e1c, [KT.BAS3]: 0x7a2c14, [KT.OBS]: 0x3a1810 }, glowSwap: { [KT.MAG]: 0xffd060, [KT.HOT]: 0xfff0a0 },
      dress: (b) => {
        crown(b, 9, 0.8, 7.55, 1.0, 1.7, 0.18, [0xffd060, 0xfff0a0, 0xff8a20], {}, true, 0.2);
        for (const s of [-1, 1]) for (let k = 0; k < 4; k++) b.add(at(new THREE.ConeGeometry(0.22, 1.4 + k * 0.3, 6), s * (2.2 + k * 0.32), 8.0 + k * 0.1, -0.5 + (k % 2) * 0.5, 0, 0, s * -(0.2 + k * 0.1)), G({ color: k % 2 ? 0xffd060 : 0xff8a20 }));
        b.add(at(new THREE.IcosahedronGeometry(1.1, 1), 0, 5.4, 1.3), G({ color: 0xffa030 }));
      },
      orbit: { n: 16, r: 4.5, y: 4.5, color: 0xffd060, size: 0.45 },
    },
  ],
  'OREHEART BEHEMOTH': [
    { // ORE-SPLIT: the strata split along their seams, ore crystals erupt across the back
      scale: 1.05, glow: 1.6, glowSwap: { [OH.GLOW]: 0xffb050 },
      dress: (b) => {
        for (const y of [4.72, 5.68, 6.58]) b.add(at(plate(4.4, 0.12, 2.9, 0.04), 0, y, 0.0, 0.12), G({ color: 0xffb050 }));
        for (let i = 0; i < 9; i++) { const x = -2.0 + i * 0.5, h = 1.2 + (i % 3) * 0.5; b.add(at(shard(0.28, h, 0.24), x, 7.2, -1.2 - (i % 2) * 0.5, -0.6 - (i % 3) * 0.15, 0, -x * 0.25), G({ color: i % 2 ? 0xffd27a : 0xffb050 })); }
      },
      orbit: { n: 8, r: 3.8, y: 7.5, color: 0xffb050, size: 0.35 },
    },
    { // MOTHERLODE: the sandstone shell crashes off; a molten iron giant studded with ore spikes
      scale: 1.12, glow: 2.0, swap: { [OH.CREAM]: 0x5a3a34, [OH.ORANGE]: 0x6a3a2a, [OH.SALMON]: 0x5a3028, [OH.MAROON]: 0x3a1c18, [OH.RUST]: 0x4a2620 }, glowSwap: { [OH.GLOW]: 0xff4a20, [OH.HOT]: 0xffc070 },
      shed: (_h, _cx, cy) => cy > 7.45,
      dress: (b) => {
        for (const [x, y, z, rx, rz, h] of [[-1.6, 7.2, -0.8, -0.5, 0.5, 1.8], [-0.5, 7.4, -1.1, -0.7, 0.1, 2.2], [0.7, 7.3, -1.0, -0.6, -0.2, 2.0], [1.7, 7.1, -0.7, -0.4, -0.6, 1.7], [-2.9, 7.4, 0, 0, 0.9, 1.4], [2.9, 7.4, 0, 0, -0.9, 1.4], [0, 7.6, 0.3, 0.2, 0, 1.3]] as const) {
          b.add(at(shard(0.34, h, 0.3), x, y, z, rx, 0, rz), { color: OH.ORE });
          b.add(at(shard(0.12, h * 0.5, 0.1), x, y + 0.1, z, rx, 0, rz), G({ color: 0xff4a20 }));
        }
        for (const [x, y, rz] of [[-1.3, 4.6, 0.6], [1.2, 5.2, -0.7], [0.4, 6.6, 0.3], [-0.6, 5.8, -0.4]] as const) b.add(at(plate(0.12, 1.4, 0.1, 0.03), x, y, 1.75, -0.12, 0, rz), G({ color: 0xff4a20 }));
      },
      orbit: { n: 14, r: 4.5, y: 5, color: 0xff4a20, size: 0.4 },
    },
  ],
};

/** a generic look for bosses without their own (keeps any future boss transforming visibly) */
const GENERIC: [FormLook, FormLook] = [
  { scale: 1.05, glow: 1.6, orbit: { n: 8, r: 3.5, y: 5, color: 0xffffff, size: 0.3 } },
  { scale: 1.1, glow: 2.0, tint: [1.1, 0.95, 0.95], orbit: { n: 12, r: 4.5, y: 5, color: 0xffffff, size: 0.4 } },
];
export function formLook(boss: string, form: number): FormLook | null {
  if (form <= 0) return null;
  return (FORM_LOOKS[boss] ?? GENERIC)[Math.min(2, form) - 1];
}
/** the art of a boss's form (form 1 or 2; 0-based form index), built from its own art */
export function formArt(base: EnemyArt, boss: string, form: number): EnemyArt {
  const L = formLook(boss, form)!;
  return { ...base, build: (elite: boolean) => applyLook(base.build(elite), L) };
}
